/* TMF633 catalog → the cockpit's `Offering` and `Characteristic`.
 *
 * The cockpit's model of the catalog is deliberately flatter than TMF's. It has
 * one list of service characteristics, and each offering names the ones its
 * specification does *not* carry. MRI is independent: case screens use the
 * `MriFieldSet` version pinned on the case.
 *
 * On the wire those are two levels: every `ServiceSpecCharacteristic` belongs to
 * exactly one `ServiceSpecification`, and a candidate points at one
 * specification. So `excludes` is not a field to read — it is the difference
 * between the union of every specification's characteristics and the one
 * specification a candidate is bound to. That subtraction happens here, from a
 * single `GET /serviceSpecCharacteristic` across all specifications, so adding an
 * offering does not add a request.
 *
 * `mandatory` is `minCardinality >= 1` and the API already computes it — the
 * same derivation, in one place, published rather than re-implemented. This file
 * reads the published field and does not recompute it.
 */

import type { components } from '@/shared/api/generated/openapi';
import { DASH } from '@/shared/api/http/map/format';
import { readable } from '@/shared/api/http/map/ids';
import type { Characteristic, Offering } from '@/shared/domain/serviceCatalog';

export type WireCharacteristic = components['schemas']['ServiceSpecCharacteristicModel'];
export type WireSpecification = components['schemas']['ServiceSpecificationModel'];
export type WireCandidate = components['schemas']['ServiceCandidateModel'];

type Dimension = Characteristic['dimension'];

const DIMENSIONS: readonly Dimension[] = ['Who', 'What', 'Why', 'Where', 'When', 'How'];

function dimensionOf(raw: string | undefined): Dimension {
  return DIMENSIONS.find((candidate) => candidate === raw) ?? 'What';
}

/* ---------- characteristics ---------- */

/** `0.90`, from whichever JSON form the confidence floor arrived in.
 *
 * The column is read as a threshold and compared by eye against the confidence
 * on a value, so the two decimal places are not decoration — `0.9` beside `0.85`
 * invites a misread that `0.90` does not. A characteristic with no floor shows a
 * dash rather than `0.00`, which would say "anything passes" when the truth is
 * "no threshold was set". */
function formatFloor(floor: number | string | null | undefined): string {
  if (floor === null || floor === undefined || floor === '') return DASH;
  const value = typeof floor === 'number' ? floor : Number(floor);
  return Number.isNaN(value) ? DASH : value.toFixed(2);
}

/** The rule a reviewer is being pointed at, by key.
 *
 * Two different keys, and they answer different questions: the conditional rule
 * decides *whether* the field applies to this order, the validation rule decides
 * whether what was entered is usable. Both can be set, and where they are, both
 * are shown — collapsing them would hide the first, which is the one that
 * explains why a field a reviewer expected is absent. */
function ruleOf(characteristic: WireCharacteristic): string {
  const keys = [characteristic.conditionalRuleKey, characteristic.validationRuleKey].filter(
    (key): key is string => Boolean(key),
  );
  return keys.length > 0 ? keys.join(' · ') : DASH;
}

/**
 * The service characteristic catalogue: every characteristic across every specification, once.
 *
 * Deduplicated by name, because the cockpit's list is a vocabulary rather than a
 * set of rows — `deliveryLocation` means the same field whichever specification
 * declares it, and `caseMri()` joins values to it by name. Two specifications
 * declaring it produce one entry, and the first one wins: the API orders by
 * `displayOrder`, so the first is the one whose catalog puts it earliest, which
 * is as good a tie-break as exists and is at least stable between requests.
 *
 * Sorted by dimension, so the Who fields sit together and the grid's grouping
 * matches what the wire happened to return in.
 */
export function toCharacteristics(
  wires: readonly WireCharacteristic[],
): readonly Characteristic[] {
  const seen = new Set<string>();
  const characteristics: Characteristic[] = [];

  for (const wire of wires) {
    if (seen.has(wire.name)) continue;
    seen.add(wire.name);
    characteristics.push({
      name: wire.name,
      // The technical name where no business name is set. A blank column would
      // read as "this field has no meaning to the process", which is a stronger
      // claim than "nobody has written the label down yet".
      businessName: wire.businessName ?? wire.name,
      dimension: dimensionOf(wire.mriDimension),
      type: wire.valueType,
      mandatory: wire.mandatory,
      cardinality: `${wire.minCardinality}..${wire.maxCardinality}`,
      rule: ruleOf(wire),
      floor: formatFloor(wire.confidenceFloor),
      // A conditional rule is what makes a field an approval or compliance
      // question — it applies above a limit, or under a policy — and that is
      // exactly what the flag is calling out. A validation rule is about format,
      // which is not a consequence anybody needs warning about.
      flagged: Boolean(wire.conditionalRuleKey),
    });
  }

  return characteristics.sort(
    (a, b) => DIMENSIONS.indexOf(a.dimension) - DIMENSIONS.indexOf(b.dimension),
  );
}

/* ---------- offerings ---------- */

/** `SSPEC-2210 v3` — the string the catalog screen links to the MRI admin by.
 *
 * `specKey` is the stable identity of the specification across its versions and
 * `version` distinguishes them, so the pair is what a person quotes when they
 * say which definition a case was judged against. The name is second choice, not
 * first: two versions of one specification usually share it, so a name alone
 * cannot say which of them a case was judged against — the version beside it is
 * what closes that gap.
 *
 * The ladder exists because `specKey` is only *documented* as a business key.
 * Some deployments populate it with the specification's uuid, and `SSPEC-2210`
 * and `6002ba57-caa5-…` are both valid `string`s, so nothing but looking at the
 * value can tell them apart. Where the key is unquotable the name is what the
 * reader gets, and where neither survives, the version alone — `v3` is thin, but
 * it is true, and every screen that shows this string shows the candidate's name
 * beside it. A uuid would be none of those things.
 *
 * Nothing here can return an identifier. That is the whole point of it. */
function serviceOf(spec: WireSpecification | undefined, fallback: string | undefined): string {
  if (!spec) return readable(fallback) ?? DASH;

  const identity = readable(spec.specKey) ?? readable(spec.name) ?? readable(fallback);
  const version = readable(spec.version);
  const stamped = version === undefined || version.startsWith('v') ? version : `v${version}`;

  if (identity !== undefined && stamped !== undefined) return `${identity} ${stamped}`;
  return identity ?? stamped ?? DASH;
}

/** Where the candidate may be ordered, in the two fields the phrase is written
 *  from.
 *
 * The regions are the answer where they are listed, and they are the catalog's
 * own names, so they travel as they arrived. Where none are listed, `placeScope`
 * is the answer instead — a candidate scoped `everywhere` is orderable anywhere,
 * and saying so is more useful than an empty cell that reads as "unknown". That
 * word is the cockpit's, so it leaves here as a flag and is written at render. */
function regionsOf(candidate: WireCandidate): Pick<Offering, 'regions' | 'globalScope'> {
  const regions = candidate.region ?? [];
  if (regions.length > 0) return { regions: regions.join(', ') };
  return {
    regions: null,
    ...(candidate.placeScope === 'global' || candidate.placeScope === 'everywhere'
      ? { globalScope: true }
      : {}),
  };
}

/** Characteristic names per specification, and the union across all of them. */
function bySpecification(
  wires: readonly WireCharacteristic[],
): { readonly perSpec: ReadonlyMap<string, ReadonlySet<string>>; readonly all: ReadonlySet<string> } {
  const perSpec = new Map<string, Set<string>>();
  const all = new Set<string>();

  for (const wire of wires) {
    const names = perSpec.get(wire.serviceSpecificationId) ?? new Set<string>();
    names.add(wire.name);
    perSpec.set(wire.serviceSpecificationId, names);
    all.add(wire.name);
  }
  return { perSpec, all };
}

/**
 * Service candidates → the offerings the catalog screen lists.
 *
 * `excludes` is computed rather than read: it is every characteristic in the
 * catalogue that this candidate's specification does not declare. That is the
 * subtraction used by catalog screens to describe the offering. It does not
 * decide MRI completeness.
 *
 * `specified: false` marks a candidate whose specification declares nothing yet.
 * The distinction matters: an offering that excludes everything and an offering
 * that has not been specified look identical in the count and are completely
 * different facts.
 *
 * A candidate bound to no specification at all is treated the same way. It
 * cannot be ordered against until somebody says what it requires, and inheriting
 * the union of every other specification's fields would invent that answer.
 */
export function toOfferings(
  candidates: readonly WireCandidate[],
  characteristics: readonly WireCharacteristic[],
  specifications: readonly WireSpecification[] = [],
): readonly Offering[] {
  const { perSpec, all } = bySpecification(characteristics);
  const specs = new Map(specifications.map((spec) => [spec.id, spec]));

  return candidates.map((candidate) => {
    const specId = candidate.serviceSpecification?.id;
    const declared = specId ? perSpec.get(specId) : undefined;
    const specified = declared !== undefined && declared.size > 0;

    return {
      id: candidate.id,
      candidate: candidate.id,
      name: candidate.name,
      category: candidate.category ?? DASH,
      service: serviceOf(
        specId ? specs.get(specId) : undefined,
        candidate.serviceSpecification?.name ?? undefined,
      ),
      lifecycle: candidate.lifecycleStatus,
      version: candidate.resourceVersion,
      ...regionsOf(candidate),
      excludes: specified ? [...all].filter((name) => !declared.has(name)) : [],
      specified,
    };
  });
}
